<!--
	@component
	Material-selection page for the whitelist modal.

	## Layout
	- Header: active lot type label + count of currently selected materials
	- Tab row: one `SwitchableTab` per `$selectedLotTypes`
	- For non-`LotType.TRANSPORT`: left column shows catalogs/material checkboxes and
	  right column shows `StaticChipList` selectors for the active material
	- For `LotType.TRANSPORT`: renders `<WhitelistTransportTab />` instead of the
	  catalog/property split layout

	## Context stores consumed
	- `whitelistConfigData: Writable<Map<LotType, WhitelistCatalog[]>>` — available
	  catalogs per lot type
	- `selectedWhitelistData: Writable<AllWhitelistData>` — user selection state
	- `selectedLotTypes: Writable<LotType[]>` — lot types chosen on the start page
	- `currentLotType: Writable<LotType>` — the active tab

	## Interface
	- `export let errorMaterials: string[]` — bidirectional via `bind:`. Populated
	  from `AllWhitelistData.checkSelectedMaterials` when a checked material has no
	  property selections
	- `export function updateActiveElementsWithError()` — imperative hook for
	  callers via `bind:this`; scrolls to and activates the first error material
	- Dispatches `materialSelectionChange` on every checkbox toggle and
	  `updateBreadcrumbs` when switching tabs

	@see {@link ./whitelistData.ts} for `AllWhitelistData` selection model
-->
<script lang="ts">
	import { t } from '$i18n/i18n'
	import { whitelistModalUtils } from './whitelistModalUtil'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'
	import { AnimateSharedLayout } from 'svelte-motion'
	import SwitchableTab from '../mmpv/SwitchableTab.svelte'
	import Checkbox from '../atoms/Checkbox.svelte'
	import StaticChipList from '../organisms/StaticChipList.svelte'
	import {
		AllWhitelistData,
		type WhitelistCatalog,
		type WhitelistMaterial,
		type WhitelistMaterialProperty,
		type WhitelistMaterialPropertyListElement
	} from './whitelistData'
	import { LotType, TooltipPlacements } from '$types/enums'
	import { createEventDispatcher, getContext, hasContext, onMount, tick } from 'svelte'
	import { writable, type Writable } from 'svelte/store'
	import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
	import { WhitelistView } from './whitelist'
	import Tooltip from '../atoms/Tooltip.svelte'
	import WhitelistTransportTab from './WhitelistTransportTab.svelte'

	// exported variables
	export let errorMaterials: string[] = []

	const dispatch = createEventDispatcher()
	const headerFontProps: FontProps = DefaultRobotoFontProps(
		'1rem',
		'0.75rem',
		undefined,
		undefined,
		500
	)

	// context stores
	let whitelistConfigData: Writable<Map<LotType, WhitelistCatalog[]>> = hasContext(
		'whitelistConfigData'
	)
		? getContext('whitelistConfigData')
		: writable(new Map<LotType, WhitelistCatalog[]>())

	let allSelectedData: Writable<AllWhitelistData> = hasContext('selectedWhitelistData')
		? getContext('selectedWhitelistData')
		: writable(new AllWhitelistData())
	let selectedTransportData: Writable<Map<LotType, Map<string, string[]>>> = hasContext(
		'selectedWhitelistTransportData'
	)
		? getContext('selectedWhitelistTransportData')
		: writable(new Map<LotType, Map<string, string[]>>())

	let selectedLotTypes: Writable<LotType[]> = hasContext('selectedLotTypes')
		? getContext('selectedLotTypes')
		: writable([])

	let selectedLotType: Writable<LotType> = hasContext('currentLotType')
		? getContext('currentLotType')
		: writable(LotType.DISPOSAL)

	let activeData: WhitelistCatalog[]
	let activeMaterial: WhitelistMaterial
	let activeCatalog: WhitelistCatalog
	let propertiesTitle: string
	let selectedMaterialsCount: number // the variable stores the amount of selected materials
	let selectedListElementIds: Map<string, string[]> // contains the ids of the selected chips for a specific material

	let ref_materialListView: HTMLElement
	let errorTooltips: Record<
		string,
		{ enable: () => void; disable: () => void; show: (force: boolean) => void }
	> = {}

	function updatePropertiesTitle() {
		propertiesTitle = $t('UI.whitelist.modal.propertiesTitle', {
			materialName: activeMaterial?.name
		})
	}

	/**
	 * Convert an item to a WhitelistMaterialPropertyListElement because e.detail.selectedList return list object as in StaticChipList.
	 *
	 * @param {any} item - The item to convert.
	 * @return {WhitelistMaterialPropertyListElement} - The converted WhitelistMaterialPropertyListElement.
	 */
	function convertToWhitelistMaterialPropertyListElement(item: {
		label: string
	}): WhitelistMaterialPropertyListElement {
		return {
			name: item.label
		}
	}

	/**
	 * Updates the active material and catalog.
	 *
	 * @param {WhitelistMaterial} material - The new active material.
	 * @param {WhitelistCatalog} catalog - The new active catalog.
	 */
	async function updatePropertiesAndElements(
		material: WhitelistMaterial,
		catalog: WhitelistCatalog
	) {
		if (activeCatalog.id !== catalog.id) {
			activeCatalog = { ...catalog }
		}
		await tick()
		activeMaterial = { ...material }
		// we changed the material, so we need to get the selectedListElements for this material
		selectedListElementIds = $allSelectedData.getPropertyListElementsPerProperty(
			$selectedLotType,
			activeCatalog,
			activeMaterial
		)
	}

	/**
	 * adds a property list item to the selection data of the user and updates the errorMaterials,
	 * if there are already any errors
	 * @param e
	 */
	function addItemToSelectedData(
		e: CustomEvent<{ selectedItem: { label: string }; identifier: WhitelistMaterialProperty }>
	) {
		let addedElement: WhitelistMaterialPropertyListElement =
			convertToWhitelistMaterialPropertyListElement(e.detail.selectedItem)
		$allSelectedData.addListElement(
			$selectedLotType,
			addedElement,
			activeCatalog,
			activeMaterial,
			e.detail.identifier
		)
		// we update the variable here, however, if we did not call the check method, the list will be empty.
		// we only check when clicking on the next button
		// we do not trigger the error checks, but simply look if there are errors left after adding the item
		errorMaterials = $allSelectedData.getErrorMaterials()
		$allSelectedData = $allSelectedData // TODO: IS - confirm we don't need destructring here
	}

	function removeItemFromSelectedData(
		e: CustomEvent<{ selectedItem: { label: string }; identifier: WhitelistMaterialProperty }>
	) {
		let removedElement: WhitelistMaterialPropertyListElement =
			convertToWhitelistMaterialPropertyListElement(e.detail.selectedItem)
		$allSelectedData.removeListElement(
			$selectedLotType,
			removedElement,
			activeCatalog,
			activeMaterial,
			e.detail.identifier
		)
		$allSelectedData = $allSelectedData // TODO: IS - confirm we don't need destructring here
	}

	/**
	 * This function handles checkbox changes. When a checkbox of a material is checked, we want to add an empty
	 * list for the material key. This should later be used to trigger the user interaction as he must chose properties
	 * for this material. If the checkbox is deselected, then we simply drop the entry, that means we also
	 * deselect all the properties that were mapped to that material.
	 * @param e
	 * @param material The material to which the checkbox belongs
	 * @param catalog The catalog to which the material belongs
	 */
	function handleMaterialSelect(
		e: CustomEvent<{ checked: boolean; id: string }>,
		material: WhitelistMaterial,
		catalog: WhitelistCatalog
	) {
		updatePropertiesAndElements(material, catalog)
		if (e.detail.checked) {
			$allSelectedData.addMaterialKey($selectedLotType, catalog, material)
		} else {
			$allSelectedData.removeMaterialKey($selectedLotType, material, catalog)
			if (material.id === activeMaterial.id) {
				// we must only update, when we deselect the activeMaterial
				selectedListElementIds = $allSelectedData.getPropertyListElementsPerProperty(
					$selectedLotType,
					catalog,
					material
				)
			}
		}
		dispatch('materialSelectionChange')
		selectedMaterialsCount = $allSelectedData.getMaterialCountForType($selectedLotType)
	}

	function updateActiveElements(): void {
		activeData = $whitelistConfigData.get($selectedLotType) || []
		if (!activeData.length) return
		activeCatalog = activeData[0]
		if (!activeCatalog?.materials?.length) return
		activeMaterial = activeCatalog.materials[0]
	}

	// export to call it from the modal when clicking on the next button
	// used by WhitelistModal
	export function updateActiveElementsWithError() {
		// we do not want to directly switch to another material in case we just selected a property for the first error material
		// we need to wait until the user manually selects another material
		// errorsReduced says that a material has been removed from the errors list and has long as no material is selected by
		// hand, we prevent updating the activeMaterial automatically
		if (errorMaterials.length > 0) {
			for (let catalog of $whitelistConfigData.get($selectedLotType) || []) {
				let errorMaterialInCatalog = catalog.materials.find((m) => m.id === errorMaterials[0])
				if (errorMaterialInCatalog) {
					activeCatalog = catalog
					activeMaterial = errorMaterialInCatalog
					let materialSpan = document.getElementById(`material-${activeMaterial.id}`)
					materialSpan?.scrollIntoView({ behavior: 'smooth' })
					return
				}
			}
		}
	}

	function handleSwitchTab(lotType: LotType) {
		// If materials are checked, validate their config before allowing tab switch
		if (getSelectionCountForType($selectedLotType) > 0) {
			errorMaterials = $allSelectedData.checkSelectedMaterials($selectedLotType)
			if (errorMaterials.length > 0) {
				activeMaterial = activeMaterial // TODO: IS - confirm we don't need destructring here
				return
			}
		}
		$selectedLotType = lotType
		// if selected lot type is in breadcrumbs -> pop breadcrumbs until breadcrumb lot type is equal selected lot type
		// if not add lot type to breadcrumb
		breadcrumbs.pop()
		const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType($selectedLotType)
		dispatch('updateBreadcrumbs', {
			title: breadcrumbTitle,
			view: WhitelistView.MATERIAL_TYPE,
			lotType
		})
	}

	// Needed to initialize the Svelte Chips
	function initializeListElements(listElements: WhitelistMaterialPropertyListElement[]) {
		return listElements.map((element) => ({
			label: element.name,
			value: element.name,
			msg: ''
		}))
	}

	function getSelectionCountForType(lotType: LotType): number {
		const materialCount = $allSelectedData.getMaterialCountForType(lotType)
		const transportSelections = $selectedTransportData.get(lotType)
		if (!transportSelections) return materialCount
		let transportCount = 0
		for (const values of transportSelections.values()) {
			transportCount += values.length
		}
		return materialCount + transportCount
	}

	function handleTransportSelectionChange() {
		dispatch('materialSelectionChange')
	}

	function showErrorTooltips() {
		let firstShown = false
		for (let errorTooltipKey in errorTooltips) {
			let currentTooltip = errorTooltips[errorTooltipKey]
			if (currentTooltip) {
				if (errorMaterials.includes(errorTooltipKey)) {
					currentTooltip.enable()
					if (!firstShown) {
						currentTooltip.show(true)
						firstShown = true
					}
				} else {
					currentTooltip.disable()
				}
			}
		}
	}

	$: if (activeMaterial) updatePropertiesTitle()
	let prevLotType: LotType | null = null
	$: if ($selectedLotType && $selectedLotType !== prevLotType) {
		prevLotType = $selectedLotType
		errorTooltips = {}
		updateActiveElements()
		updatePropertiesTitle()
		tick().then(() => showErrorTooltips())
	}
	$: selectedMaterialsCount = $allSelectedData ? getSelectionCountForType($selectedLotType) : 0
	$: selectedListElementIds =
		$allSelectedData && activeCatalog && activeMaterial
			? $allSelectedData.getPropertyListElementsPerProperty(
					$selectedLotType,
					activeCatalog,
					activeMaterial
				)
			: new Map<string, string[]>()
	$: if (errorMaterials) showErrorTooltips()

	onMount(() => {
		updateActiveElements()
	})
</script>

<div class="whitelist-view-materialType">
	<div class="whitelist-view-header">
		<span>
			{$t('UI.whitelist.modal.materialType.materialHeader', {
				prefix: whitelistModalUtils.getPrefix($selectedLotType),
				materialType: whitelistModalUtils.getLotTypeStringResource($selectedLotType)
			})}
		</span>
		{#if $selectedLotType !== LotType.TRANSPORT}
			<span id="materialsCount"
				>{selectedMaterialsCount === 1
					? $t('UI.whitelist.modal.selectedMaterialsCount.singular')
					: $t('UI.whitelist.modal.selectedMaterialsCount.plural', {
							number: selectedMaterialsCount
						})}</span
			>
		{/if}
	</div>
	<nav class="whitelist-view-nav">
		<ul>
			<AnimateSharedLayout>
				{#each $selectedLotTypes as lotType (lotType)}
					<SwitchableTab
						active={$selectedLotType === lotType}
						fontProps={DefaultRobotoFontProps('0.625', '0.75rem', '#9d9d9d', 'black', 400)}
						label={whitelistModalUtils.getLotTypeStringResource(lotType)}
						on:click={() => handleSwitchTab(lotType)}
					/>
				{/each}
			</AnimateSharedLayout>
		</ul>
	</nav>
	{#if $selectedLotType === LotType.TRANSPORT}
		<div class="whitelist-view-material-list">
			<WhitelistTransportTab on:transportSelectionChange={handleTransportSelectionChange} />
		</div>
	{:else}
		<div bind:this={ref_materialListView} class="whitelist-view-material-list">
			<div>
				{#each activeData || [] as catalog (catalog.id)}
					<h4 class="catalog-title">{catalog.name}</h4>
					<ul class="material-list">
						{#each catalog.materials as material (material.id)}
							{@const active = material.id === activeMaterial?.id}
							{@const hasError = errorMaterials.includes(material.id)}
							<li>
								<Tooltip
									bind:this={errorTooltips[material.id]}
									placementTtip={TooltipPlacements.BOTTOM_START}
									msg={$t('UI.whitelist.materialSelect.error.tt')}
									maxWidthTtip={350}
									appendToObject={ref_materialListView}
									removeCloseIconAfterClose
									isHtml
									trigger="manual"
								>
									<span
										id="material-{material.id}"
										class="material-name"
										class:active
										class:error={hasError}
										on:click={() => updatePropertiesAndElements(material, catalog)}
									>
										{material.name}
									</span>
								</Tooltip>
								<Checkbox
									id={'ctlg-' + catalog.id + '-mtrl-' + material.id}
									labelFontProps={headerFontProps}
									checked={$allSelectedData.hasMaterialForType(
										$selectedLotType,
										catalog.id,
										material.id
									)}
									on:checkboxChange={(e) => handleMaterialSelect(e, material, catalog)}
								/>
							</li>
							<li class="separator"></li>
						{/each}
					</ul>
				{/each}
			</div>
			<div>
				<h4 class="properties-title">{propertiesTitle}</h4>
				{#each activeMaterial?.properties || [] as property (property.name)}
					<StaticChipList
						id={'mtrl-' + activeMaterial.id + '-prprty-' + property.name}
						identifier={property}
						header={property.name}
						fontProps={headerFontProps}
						activeChips={selectedListElementIds.get(property.name)}
						list={initializeListElements(property.propertyListElements)}
						headerPaddingBottom="1 rem"
						on:addItem={addItemToSelectedData}
						on:removeItem={removeItemFromSelectedData}
						showSelectAll
					/>
				{/each}
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	@use '$styles/global';

	.whitelist-view-materialType {
		display: grid;
		grid-template-rows: max-content;
		gap: 10px;
		width: 100%;

		& > div.whitelist-view-header {
			@include global.flex-row($justify: space-between);

			& > span:first-child {
				@include global.roboto-font(1rem, 500, 0.75rem, black);
			}

			& > span#materialsCount {
				@include global.roboto-font(undefined, 500, 0.875rem, #5888c0);
			}
		}

		& > nav.whitelist-view-nav {
			background: white;
			border-radius: 10px 10px 0 0;
			border-bottom: 1px solid #e9e9e9;
			height: 33px;

			& > ul {
				display: flex;
				width: max-content;
				padding-left: 6px;
				list-style: none;
				direction: ltr;
				height: 100%;
			}
		}

		& > div.whitelist-view-material-list {
			@include global.flex-row(2.625rem, $justify: space-between);

			position: relative;
			overflow: hidden;

			& > div {
				@extend .scrollable;

				height: 21.75rem;
				overflow-x: hidden;
				width: 100%;

				&:first-child {
					padding-left: 0.875rem;
					max-width: 250px;
					overflow-y: scroll;
					transform: scaleX(-1);
				}
			}
		}
	}

	.catalog-title {
		@include global.roboto-font(18px, 400, 0.875rem, #000);

		background: linear-gradient(
			90deg,
			rgba(192, 192, 192, 0.2) 99.99%,
			rgba(217, 217, 217, 0) 100%
		);
		width: 100%;
		height: 2.063rem;
		padding: 10px 40px 10px 14px;
		transform: scaleX(
			-1
		); // reverse the scale from the parent div that is necessary because of the scrollbar on the left
	}

	.properties-title {
		@include global.roboto-font(18px, 500, 0.875rem, #000);

		text-align: start;
		width: 26.5rem;
		margin-top: 10px;
		margin-bottom: auto;
	}

	.material-list {
		padding: 0;
		list-style: none none;
		transform: scaleX(-1);

		& span {
			width: 100%;
			text-align: left;
			padding-left: 14px;

			@include global.roboto-font(18px, 400, 0.75rem, #000);

			&.active {
				color: global.$primaryGreen;
				font-weight: 500;
			}

			&.error {
				color: global.$primaryRed;
				font-weight: 500;
			}

			&:hover {
				color: #58c079;
				cursor: pointer;
			}
		}

		& > li:nth-child(odd) {
			@include global.flex-row(1rem, $justify: space-between);

			margin-bottom: 5px;
			margin-top: 5px;

			& :global(div.tooltip-container-slot) {
				display: flex;
			}
		}

		& > li.separator {
			border-top: 1px solid #c0c0c033;
			line-height: 0;
			margin-top: 5px;
		}
	}
</style>
